import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "./electricalApi";

/**
 * The write-up pane — pass two, done sitting down. Modeled on
 * Csa/CsaWriteUp.js.
 *
 * Only non-compliant checkpoints appear. A YES needs nothing written: the
 * checklist already supplies its wording, so asking an auditor to type
 * against hundreds of Yeses would put the work straight back that pass one
 * removed.
 *
 * Under each gap sit that auditor's own unused notes, tagged category first,
 * then newest. No keyword matching and no AI — they recognise their own note
 * in a second, and a system guessing wrongly is worse than a plain list.
 */
export default function ElectricalWriteUp({ auditId, framework, readOnly, onObservation, onNotesChanged, onJump }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyUnwritten, setOnlyUnwritten] = useState(false);

  /*
   * getGaps() itself carries no checkpoint text (only `statement`, the
   * default finding wording) - the framework already loaded for the
   * assessment screen is the real source for what the checkpoint actually
   * asks, keyed by code for a cheap lookup per gap.
   */
  const checkpointTextByCode = useMemo(() => {
    const m = {};
    (framework?.areas || []).forEach((a) =>
      (a.categories || []).forEach((cat) =>
        (cat.checkpoints || []).forEach((cp) => {
          m[cp.code] = cp.check_points;
        })
      )
    );
    return m;
  }, [framework]);

  const load = useCallback(() => {
    setBusy(true);
    api
      .getGaps(auditId)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e.message))
      .finally(() => setBusy(false));
  }, [auditId]);

  useEffect(load, [load]);

  /** Attaching copies the note's text in — it does not link it. */
  const attach = async (noteId, code) => {
    try {
      const r = await api.useNote(auditId, noteId, code);
      setData((prev) => patchGap(prev, code, { observation: r.observation, written: true }, noteId));
      if (onNotesChanged) {
        api
          .listNotes(auditId)
          .then((rows) => onNotesChanged((rows || []).filter((n) => !n.used_for).length))
          .catch(() => {});
      }
    } catch (e) {
      setError(e.message);
    }
  };

  const saveText = (code, text) => {
    onObservation(code, text);
    setData((prev) => patchGap(prev, code, { observation: text, written: !!text.trim() }));
  };

  if (error) {
    return (
      <div className="cap-writeup">
        <p className="wu-error">{error}</p>
        <button type="button" className="small-btn" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  if (!data) return <div className="cap-loading">Loading the gaps…</div>;

  if (data.total === 0) {
    return (
      <div className="cap-writeup">
        <div className="wu-empty">
          <h2>Nothing to write up yet</h2>
          <p>
            This screen collects the gaps —  every checkpoint answered <b>No</b> or
            <b>Partial</b> — so you can write them up at the desk instead of while
            standing in a plant.
          </p>
          <p>
            Nothing has been marked anything but compliant on this audit yet,
            so there is nothing here. Compliant checkpoints never need
            writing: the checklist supplies their wording.
          </p>
          {onJump ? (
            <button type="button" className="cap-btn" onClick={() => onJump(null, null)}>
              Back to the assessment
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  const shown = onlyUnwritten ? data.gaps.filter((g) => !g.written) : data.gaps;
  const left = data.total - data.written;
  console.log(shown);
  return (
    <div className="cap-writeup">
      <div className="wu-head">
        <div>
          <h2>Write-up</h2>
          <p>
            {data.written} of {data.total} gaps written
            {left ? ` · ${left} still need a note before you can submit` : " · all done"}
          </p>
        </div>
        <label className="wu-filter">
          <input
            type="checkbox"
            checked={onlyUnwritten}
            onChange={(e) => setOnlyUnwritten(e.target.checked)}
          />
          Show only what still needs writing
        </label>
        <button type="button" className="small-btn" onClick={load} disabled={busy}>
          {busy ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {shown.length === 0 ? (
        <div className="wu-empty">
          <h2>Every gap is written up</h2>
          <p>Nothing is blocking submit from this screen.</p>
        </div>
      ) : null}

      {shown.map((g) => (
        <Gap
          key={g.code}
          gap={g}
          checkpointText={checkpointTextByCode[g.code]}
          readOnly={readOnly}
          onSave={saveText}
          onAttach={attach}
          onJump={onJump}
        />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Gap({ gap, checkpointText, readOnly, onSave, onAttach, onJump }) {
  const [draft, setDraft] = useState(gap.comment || "");
  const timer = useRef(null);
  const dirty = useRef(false);

  useEffect(() => {
    if (dirty.current) return;
    setDraft(gap.comment || "");
  }, [gap.comment]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const flush = (text) => {
    clearTimeout(timer.current);
    if (!dirty.current) return;
    dirty.current = false;
    onSave(gap.code, text);
  };

  const type = (e) => {
    const text = e.target.value;
    setDraft(text);
    dirty.current = true;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(text), 900);
  };
  console.log('gap:',gap)
  return (
    <article className={"wu-gap" + (gap.written ? " done" : "")}>
      <header>
        <button
          type="button"
          className="wu-code"
          title="Open this checkpoint on the assessment screen"
          onClick={() => onJump(gap.code, gap.area, gap.category)}
        >
          {gap.code}
        </button>
        {/* The checkpoint itself - what was actually being checked, not the
            finding statement below - so a gap reads as complete without
            having to flip back to the assessment screen to see what it was. */}
        <span className="wu-checkpoint-text">
          {gap.check_points || gap.checkpoint || checkpointText}
        </span>
        <span className={"wu-status s-" + String(gap.status || "").toLowerCase().replace(/[^a-z]/g, "")}>
          {gap.status}
        </span>
        <span className="wu-where">
          {gap.area} · {gap.category}
        </span>
        {gap.written ? <span className="wu-tick">written</span> : null}
      </header>

      {/*
       * The checklist's own wording for a failed check. It is the starting
       * point, not the finished text — what the auditor writes below is what
       * reaches the client.
       */}
      <p className="wu-statement">{gap.statement}</p>

      <textarea
        className="wu-text"
        rows={3}
        value={draft}
        disabled={readOnly}
        placeholder="What you actually saw at this site."
        onChange={type}
        onBlur={() => flush(draft)}
      />

      {gap.recommendations ? (
        <p className="wu-rec">
          <strong>Recommendation</strong>
          <br />
          {gap.recommendations}
        </p>
      ) : null}

      {gap.suggested_notes && gap.suggested_notes.length ? (
        <div className="wu-notes">
          <div className="wu-notes-h">From your note pad</div>
          {gap.suggested_notes.map((n) => (
            <div className="wu-note" key={n.id}>
              <p>{n.body}</p>
              <button
                type="button"
                className="small-btn"
                disabled={readOnly}
                onClick={() => onAttach(n.id, gap.code)}
              >
                Use
              </button>
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Update one gap in place rather than re-fetching.
 *
 * A re-fetch would reorder the list under the auditor's cursor and would not
 * show an edit that has not synced yet. `usedNoteId` removes the note from
 * every gap's suggestions, since a used note stops being offered anywhere.
 */
function patchGap(prev, code, patch, usedNoteId) {
  if (!prev) return prev;
  const gaps = prev.gaps.map((g) => {
    const next = g.code === code ? { ...g, ...patch } : g;
    if (!usedNoteId) return next;
    return {
      ...next,
      suggested_notes: (next.suggested_notes || []).filter((n) => n.id !== usedNoteId),
    };
  });
  return { ...prev, gaps, written: gaps.filter((g) => g.written).length };
}
